import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LogoFull } from "./Logo";
import { NAV_LINKS } from "@/data/content";
import { useBooking, scrollToSection } from "@/App";

export default function Navbar() {
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ivory/85 backdrop-blur-xl border-b border-botanical/10 shadow-card"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[76px]">
          <button onClick={() => go("home")} aria-label="Aarogyam Bharat — home" className="shrink-0">
            <LogoFull />
          </button>

          <nav className="hidden xl:flex items-center gap-5" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-xs font-semibold tracking-wide text-inkbody hover:text-sage transition-colors duration-300 whitespace-nowrap"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.04, y: -1 }}
              whileTap={{ scale: 0.97 }}
              onClick={openBooking}
              data-testid="nav-book-discovery-call-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-forest text-ivory px-5 py-2.5 text-xs font-bold tracking-wide hover:bg-botanical transition-colors duration-300"
            >
              BOOK FREE DISCOVERY CALL <ArrowRight size={14} />
            </motion.button>
            <button
              onClick={() => setOpen(!open)}
              data-testid="nav-mobile-menu-button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              className="xl:hidden p-2 rounded-full text-forest hover:bg-forest/5"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden bg-ivory/95 backdrop-blur-xl border-b border-botanical/10"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="text-left font-serif text-2xl text-forest py-2.5 border-b border-forest/5 hover:text-sage transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  openBooking();
                }}
                data-testid="mobile-nav-book-button"
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-forest text-ivory px-6 py-4 text-sm font-bold tracking-wide"
              >
                BOOK YOUR FREE 1:1 DISCOVERY CALL <ArrowRight size={16} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
